/**
 * "Your session will expire soon" — the cheap half of work-in-progress protection (see the auth
 * spec's own reasoning): recovering a session after it has already ended risks a duplicate
 * submission the idempotency layer may or may not catch depending on the action, so the actual
 * fix is warning early enough that a real person can finish what they were doing, not silently
 * resurrecting state after the fact.
 *
 * Polls the in-memory access token's own expiry once a second rather than subscribing to
 * anything — `tokenStore` has no event emitter, and a token's expiry does not change between
 * renewals, so a second of staleness in the countdown costs nothing.
 */

import { useEffect, useState } from 'react';

import { getAccessTokenExpiresAt } from './tokenStore.js';

const POLL_MS = 1_000;

export type SessionExpiryWarning = {
  /** True only inside the warning window, and only while a token is actually held. */
  isExpiringSoon: boolean;
  /** Whole seconds left, clamped to zero. `null` when no token is held at all. */
  secondsRemaining: number | null;
};

/**
 * @param warnBeforeMs How long before expiry the warning turns on. Default 2 minutes — long
 * enough to finish a thought, short enough not to nag on every ordinary 15-minute token life.
 */
export function useSessionExpiryWarning(warnBeforeMs = 120_000): SessionExpiryWarning {
  const [state, setState] = useState<SessionExpiryWarning>({ isExpiringSoon: false, secondsRemaining: null });

  useEffect(() => {
    const tick = () => {
      const expiresAt = getAccessTokenExpiresAt();

      if (expiresAt === 0) {
        setState({ isExpiringSoon: false, secondsRemaining: null });
        return;
      }

      const remainingMs = expiresAt - Date.now();
      const secondsRemaining = Math.max(0, Math.round(remainingMs / 1000));

      setState({ isExpiringSoon: remainingMs > 0 && remainingMs <= warnBeforeMs, secondsRemaining });
    };

    tick();
    const interval = setInterval(tick, POLL_MS);
    return () => {
      clearInterval(interval);
    };
  }, [warnBeforeMs]);

  return state;
}
